import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { customerMasterAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  Plus, Upload, Download, Trash2, Edit, RotateCcw, Users, Search,
  AlertCircle, Ban, MapPin
} from 'lucide-react';

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC"
];

const emptyAddress = { street: '', street2: '', city: '', state: '', zip_code: '', country: 'USA' };

const emptyForm = {
  name: '', email: '', phone: '',
  home_address: { ...emptyAddress },
  delivery_same_as_home: true,
  delivery_address: { ...emptyAddress },
  discount_percent: '0', markup_percent: '0',
  remarks: '', order_blocked: false,
};

const AddressForm = ({ label, value, onChange, testPrefix }) => (
  <div className="space-y-3">
    <p className="text-sm font-bold text-slate-700">{label}</p>
    <div className="grid grid-cols-1 gap-3">
      <div>
        <Label className="text-xs">Street Address</Label>
        <Input value={value.street} onChange={(e) => onChange({ ...value, street: e.target.value })}
          placeholder="123 Main St" data-testid={`${testPrefix}-street`} />
      </div>
      <div>
        <Label className="text-xs">Street Address Line 2</Label>
        <Input value={value.street2} onChange={(e) => onChange({ ...value, street2: e.target.value })}
          placeholder="Apt, Suite, Unit (optional)" data-testid={`${testPrefix}-street2`} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">City</Label>
          <Input value={value.city} onChange={(e) => onChange({ ...value, city: e.target.value })}
            placeholder="Houston" data-testid={`${testPrefix}-city`} />
        </div>
        <div>
          <Label className="text-xs">State</Label>
          <Select value={value.state} onValueChange={(v) => onChange({ ...value, state: v })}>
            <SelectTrigger className="h-9" data-testid={`${testPrefix}-state`}><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent className="max-h-60">
              {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">ZIP Code</Label>
          <Input value={value.zip_code} onChange={(e) => onChange({ ...value, zip_code: e.target.value })}
            placeholder="77001" maxLength={10} data-testid={`${testPrefix}-zip`} />
        </div>
      </div>
    </div>
  </div>
);

const formatAddr = (a) => {
  if (!a || !a.street) return '-';
  const parts = [a.street, a.street2, a.city, a.state, a.zip_code].filter(Boolean);
  return parts.join(', ');
};

const CustomerMaster = () => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  const [form, setForm] = useState({ ...emptyForm });
  const [formLoading, setFormLoading] = useState(false);

  const load = async () => {
    try {
      const data = await customerMasterAPI.getAll(showDeleted);
      setCustomers(data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load customers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [showDeleted]);

  const resetForm = () => setForm({ ...emptyForm, home_address: { ...emptyAddress }, delivery_address: { ...emptyAddress } });

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast({ title: 'Required', description: 'Name, email, and phone are required', variant: 'destructive' });
      return;
    }
    setFormLoading(true);
    try {
      await customerMasterAPI.create({
        ...form,
        discount_percent: parseFloat(form.discount_percent) || 0,
        markup_percent: parseFloat(form.markup_percent) || 0,
      });
      toast({ title: 'Created', description: `Customer ${form.name} created` });
      setShowAdd(false);
      resetForm();
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create', variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    setFormLoading(true);
    try {
      await customerMasterAPI.update(editId, {
        ...form,
        discount_percent: parseFloat(form.discount_percent) || 0,
        markup_percent: parseFloat(form.markup_percent) || 0,
      });
      toast({ title: 'Updated', description: `Customer updated` });
      setEditId(null);
      resetForm();
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update', variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await customerMasterAPI.delete(deleteId);
      toast({ title: 'Deleted', description: `Customer flagged as deleted` });
      setDeleteId(null);
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handleRestore = async (id) => {
    try {
      await customerMasterAPI.restore(id);
      toast({ title: 'Restored' });
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await customerMasterAPI.uploadCustomers(file);
      setUploadResult(result);
      toast({ title: 'Upload Complete', description: result.message });
      load();
    } catch (error) {
      toast({ title: 'Upload Failed', description: error.response?.data?.detail || 'Error', variant: 'destructive' });
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const openEdit = (c) => {
    setForm({
      name: c.name, email: c.email, phone: c.phone,
      home_address: c.home_address || { ...emptyAddress },
      delivery_same_as_home: c.delivery_same_as_home ?? true,
      delivery_address: c.delivery_address || { ...emptyAddress },
      discount_percent: String(c.discount_percent || 0),
      markup_percent: String(c.markup_percent || 0),
      remarks: c.remarks || '',
      order_blocked: c.order_blocked || false,
    });
    setEditId(c.customer_id);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) ||
      c.customer_id?.toLowerCase().includes(q) || c.phone?.includes(q);
  });

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" /></div></Layout>;
  }

  const formDialog = (isEdit) => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input value={form.name} maxLength={50} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="John Smith" data-testid="form-name" />
        </div>
        <div className="space-y-2">
          <Label>Email *</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="john@example.com" data-testid="form-email" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Phone *</Label>
        <Input value={form.phone} maxLength={20} onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="(555) 123-4567" data-testid="form-phone" />
      </div>

      <AddressForm label="Home Address" value={form.home_address}
        onChange={(v) => setForm({ ...form, home_address: v })} testPrefix="home" />

      <div className="flex items-center gap-2 py-1">
        <Checkbox id="same-addr" checked={form.delivery_same_as_home}
          onCheckedChange={(v) => setForm({ ...form, delivery_same_as_home: v, delivery_address: v ? form.home_address : form.delivery_address })}
          data-testid="same-as-home-checkbox" />
        <label htmlFor="same-addr" className="text-sm font-medium text-slate-700 cursor-pointer">Delivery address same as home</label>
      </div>

      {!form.delivery_same_as_home && (
        <AddressForm label="Delivery Address" value={form.delivery_address}
          onChange={(v) => setForm({ ...form, delivery_address: v })} testPrefix="delivery" />
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Discount %</Label>
          <Input type="number" min="0" max="100" step="0.01" value={form.discount_percent}
            onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
            data-testid="form-discount" />
        </div>
        <div className="space-y-2">
          <Label>Markup %</Label>
          <Input type="number" min="0" max="100" step="0.01" value={form.markup_percent}
            onChange={(e) => setForm({ ...form, markup_percent: e.target.value })}
            data-testid="form-markup" />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Remarks</Label>
        <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
          placeholder="Notes about this customer..." data-testid="form-remarks" />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox id="block-order" checked={form.order_blocked}
          onCheckedChange={(v) => setForm({ ...form, order_blocked: v })}
          data-testid="form-block-order" />
        <label htmlFor="block-order" className="text-sm font-medium text-red-600 cursor-pointer">Block orders for this customer</label>
      </div>

      <Button onClick={isEdit ? handleUpdate : handleCreate} disabled={formLoading}
        className="w-full bg-gradient-to-r from-sky-500 to-teal-500" data-testid="form-submit-btn">
        {formLoading ? 'Saving...' : isEdit ? 'Update Customer' : 'Add Customer'}
      </Button>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-5" data-testid="customer-master-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="page-title">
              <Users className="h-6 w-6 text-sky-600" /> Customer Master Data
            </h1>
            <p className="text-sm text-gray-500 mt-1">{filtered.length} customers</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => { resetForm(); setShowAdd(true); }}
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
              data-testid="add-customer-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Customer
            </Button>
            <Button size="sm" variant="outline" onClick={async () => {
              try { await customerMasterAPI.downloadTemplateBlob(); } catch { toast({ title: 'Error', description: 'Download failed', variant: 'destructive' }); }
            }} data-testid="download-template-btn">
              <Download className="h-4 w-4 mr-1" /> Template
            </Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
              data-testid="upload-btn">
              <Upload className="h-4 w-4 mr-1" /> Upload Excel
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileUpload} />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search by name, email, ID, phone..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="search-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)}
              className="rounded border-gray-300" data-testid="show-deleted-checkbox" />
            Show deleted
          </label>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border shadow-sm overflow-x-auto" data-testid="customers-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <th className="px-3 py-2.5 text-left">Customer ID</th>
                <th className="px-3 py-2.5 text-left">Name</th>
                <th className="px-3 py-2.5 text-left">Email</th>
                <th className="px-3 py-2.5 text-left">Phone</th>
                <th className="px-3 py-2.5 text-left">City</th>
                <th className="px-3 py-2.5 text-center">Disc.%</th>
                <th className="px-3 py-2.5 text-center">Markup%</th>
                <th className="px-3 py-2.5 text-center">Status</th>
                <th className="px-3 py-2.5 text-left">Changed</th>
                <th className="px-3 py-2.5 text-center w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-gray-400">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="font-medium text-sm">No customers found</p>
                  </td>
                </tr>
              ) : (
                filtered.map(c => {
                  const isExpanded = expandedId === c.customer_id;
                  const city = c.home_address?.city || '';
                  return (
                    <React.Fragment key={c.customer_id}>
                      <tr className={`hover:bg-slate-50 transition-colors cursor-pointer ${c.deletion_flag ? 'opacity-50 bg-red-50/30' : ''}`}
                        data-testid={`customer-row-${c.customer_id}`}
                        onClick={() => setExpandedId(isExpanded ? null : c.customer_id)}>
                        <td className="px-3 py-2 font-mono text-xs font-bold text-slate-700 whitespace-nowrap">
                          {c.customer_id}
                          {c.deletion_flag && <span className="ml-1 text-[10px] text-red-500 bg-red-50 px-1 py-0.5 rounded">DEL</span>}
                        </td>
                        <td className="px-3 py-2 font-semibold text-slate-800 max-w-[150px] truncate">
                          {c.name}
                          {c.order_blocked && <Ban className="h-3 w-3 text-red-500 inline ml-1" title="Orders blocked" />}
                        </td>
                        <td className="px-3 py-2 text-slate-500 max-w-[160px] truncate">{c.email}</td>
                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{c.phone}</td>
                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{city || '-'}</td>
                        <td className="px-3 py-2 text-center font-medium text-teal-700">{c.discount_percent}%</td>
                        <td className="px-3 py-2 text-center font-medium text-amber-700">{c.markup_percent}%</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block w-2 h-2 rounded-full ${c.order_blocked ? 'bg-red-500' : 'bg-green-500'}`}
                            title={c.order_blocked ? 'Blocked' : 'Active'} />
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-400 whitespace-nowrap">{formatDate(c.changed_at)}</td>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-0.5">
                            {!c.deletion_flag ? (
                              <>
                                <button onClick={() => openEdit(c)} className="p-1 rounded hover:bg-sky-50 text-sky-600"
                                  data-testid={`edit-btn-${c.customer_id}`}><Edit className="h-3.5 w-3.5" /></button>
                                <button onClick={() => setDeleteId(c.customer_id)} className="p-1 rounded hover:bg-red-50 text-red-500"
                                  data-testid={`delete-btn-${c.customer_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                              </>
                            ) : (
                              <button onClick={() => handleRestore(c.customer_id)} className="p-1 rounded hover:bg-green-50 text-green-600"
                                data-testid={`restore-btn-${c.customer_id}`}><RotateCcw className="h-3.5 w-3.5" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded details */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={10} className="bg-slate-50 border-t px-4 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-bold text-slate-400 uppercase mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Home Address</p>
                                <p className="text-sm text-slate-700">{formatAddr(c.home_address)}</p>
                              </div>
                              <div>
                                <p className="text-xs font-bold text-slate-400 uppercase mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Delivery Address</p>
                                <p className="text-sm text-slate-700">
                                  {c.delivery_same_as_home ? <span className="italic text-slate-500">Same as home</span> : formatAddr(c.delivery_address)}
                                </p>
                              </div>
                            </div>
                            {c.remarks && (
                              <div className="mt-2">
                                <span className="text-xs font-bold text-slate-400">Remarks</span>
                                <p className="text-sm text-slate-600 mt-0.5">{c.remarks}</p>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add Customer</DialogTitle></DialogHeader>
          {formDialog(false)}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editId} onOpenChange={() => setEditId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit Customer — {editId}</DialogTitle></DialogHeader>
          {formDialog(true)}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Customer</DialogTitle>
            <DialogDescription>Customer <strong>{deleteId}</strong> will be flagged as deleted. The record can be restored later.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">Delete Customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Result */}
      <Dialog open={!!uploadResult} onOpenChange={() => setUploadResult(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Results</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-4">
              <div className="flex-1 p-3 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">{uploadResult?.created || 0}</p>
                <p className="text-xs text-green-600">Created</p>
              </div>
              <div className="flex-1 p-3 bg-sky-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-sky-700">{uploadResult?.updated || 0}</p>
                <p className="text-xs text-sky-600">Updated</p>
              </div>
            </div>
            {uploadResult?.errors?.length > 0 && (
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-xs font-bold text-red-700 mb-1 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> Errors</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {uploadResult.errors.map((err, i) => <p key={i} className="text-xs text-red-600">{err}</p>)}
                </div>
              </div>
            )}
            <Button onClick={() => setUploadResult(null)} className="w-full" variant="outline">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CustomerMaster;
